import SoundManager from "../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import LanguagePack from "../../../../lib/html5-software-library/src/ranj/language/LanguagePack";
import LayoutManager from "../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import ScreenNavigatorEvents from "../../../../lib/html5-software-library/src/ranj/ui/ScreenNavigatorEvents";
import AbstractContextObject from "../core/AbstractContextObject";
import SkinFactory from "./SkinFactory";
import Button from "../../../../lib/html5-software-library/src/ranj/components/buttons/Button";
import StageEvent from "../../../../lib/html5-software-library/src/ranj/performance/StageEvent";
import CacheUtil from "../../../../lib/html5-software-library/src/ranj/util/CacheUtil";
import Slice9Button from "../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Button";
/**
 * AbstractMediator.js
 * Extends AbstractContextObject
 * 
 * Contains convenience methods for the following:
 * - managing buttons
 * - managing skins + their mediators
 * 
 * @author Reinier Feijen for Ranj
 **/
export default class AbstractMediator extends AbstractContextObject {
    /**
     * 
     * @param {createjs.MovieClip} target 
     * @param {somajs.EventDispatcher} dispatcher 
     * @param {LanguagePack} languagePack 
     * @param {LayoutManager} layoutManager 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, languagePack, layoutManager, soundManager, appModel) {
        super(dispatcher, languagePack, soundManager, appModel);
        this._target = target;
        this._layoutManager = layoutManager;
        this._appModel = appModel;
        this._anchoredObjects = {};
    }

    initialize() {
        //workaround for the issue that movieclip's children are not populated instantly (mcs in asset js contain '.wait(1)' for some reason)
        if (this._target.gotoAndStop != null) this._target.gotoAndStop(0);
    }

    destroy() {
        super.destroy();
        //Unregister all anchored objects
        for (const id in this._anchoredObjects) {
            this._layoutManager.deregisterObject(this._anchoredObjects[id]);
        }

        //Destroy all buttons that were created using createButton.
        if (this._buttons) {
            for (let i = 0; i < this._buttons.length; i++) {
                this._buttons[i].destroy();
            }
        }
        //Remove all displayobjects that were added to a stage using addDisplayObjectToStage
        if (this._displayObjectsAddedToStage) {
            for (let i = 0; i < this._displayObjectsAddedToStage.length; i++) {
                this._dispatcher.dispatch(StageEvent.REMOVE_FROM_STAGE, { asset: this._displayObjectsAddedToStage[i] });
            }
        }
    }


    /**
     * Convenience method for adding skins and their mediators
     * Needs:
     *  this._dispatcher
     *  this._target reference to skin
     * NB:
     * We also need to call EVENT_REMOVED_FROM_STAGE to destroy and remove mediator\22
     *  See: removeChildFromParent
     * @param {String} skinType
     * @param {Container} parent Optional parent container. When undefined, this._taret will be used.
     * @param {Object} data Optional object whose properties will be copied to newly created skin.
     * @returns {type} skin
     */
    addChildOfType(skinType, parent, data) {
        //Create the skin using the SkinFactory
        var skin = SkinFactory.getSkin(skinType);
        //Set skinType on skin, if not already set by SkinFactory
        if (!skin.skinType)
            skin.skinType = skinType;
        //Add to target/parent
        return this.addSkin(skin, parent, data);
    }
    /**
     * Convenience method for adding skins and their mediators
     * Requires: this._dispatcher, this._target
     * Uses: ranj.ScreenNavigatorEvents
     * NB:
     * To clean up, we need to call REMOVED_FROM_STAGE to destroy and remove mediator
     *  See: removeChildFromParent
     * @param {DisplayObject} skin 
     * @param {Container} parent Optional parent container. When undefined, this._target will be used.
     * @param {Object} data Optional object whose properties will be copied to newly created skin.
     * @returns {type} skin
     */
    addSkin(skin, parent, data) {
        //Add to target/parent
        if (!parent) parent = this._target;
        parent.addChild(skin);
        //Copy data properties to skin
        if (data) {
            for (var s in data) skin[s] = data[s];
        }
        //Create the mediator, see ranj.MediatorManager.registerMediator
        this._dispatcher.dispatch(ScreenNavigatorEvents.ADDED_TO_STAGE, { data: skin });
        return skin;
    }

    removeChildFromParent(skin) {
        this._dispatcher.dispatch(ScreenNavigatorEvents.REMOVED_FROM_STAGE, { data: skin });
        if (skin.parent) skin.parent.removeChild(skin);
    }


    /**
     * Convenience method for registering display objects to layout manager.
     * Objects will automatically be deregistered on destroy,
     * or can be deregistered manually by calling _unanchor(target)
     */
    anchorObject(target, anchor, fillArea) {
        this._layoutManager.registerObject(target, anchor, fillArea);
        this._anchoredObjects[target.id] = target;
    }

    unanchorObject(target, resetPosition = false) {
        this._layoutManager.deregisterObject(target, resetPosition);
        delete this._anchoredObjects[target.id];
    }


    /**
     * Convenience method for adding skins/children to different stages
     * Used in combination with StageManager/StageCommand scheme
     * Uses: ranj.StageEvent
     * @param {DisplayObject} skin 
     * @param {String} stageId from StageID, stage to add skin to.
     * @returns
     **/
    addDisplayObjectToStage(skin, stageId) {
        //store skin so we can remove it when destroying
        if (!this._displayObjectsAddedToStage)
            this._displayObjectsAddedToStage = [];
        if (!this._displayObjectsAddedToStage.contains(skin))
            this._displayObjectsAddedToStage.push(skin);
        //
        this._dispatcher.dispatch(StageEvent.ADD_TO_STAGE, { asset: skin, stageID: stageId });
    }


    /**
     * Convenience method for caching a displayobject
     * Uses: CacheUtil
     * 
     * @param {DisplayObject} skin
     * @param {Number} marginH Optional horizontal margin, for instance for accomodating blurs
     * @param {Number} marginV Optional vertical margin, for instance for accomodating blurs 
     * @returns {undefined}
     */
    cache(skin, marginH, marginV) {
        CacheUtil.cache(skin, marginH, marginV);
    }

    uncache(skin) {
        CacheUtil.uncache(skin);
    }



    /**
     * Convenience method for creating a Button
     * @param {DisplayObject} skin
     * @param {Function} boundClickHandler Optional handler method for click event.
     * @param {String} label Optional text label.
     * @param {type} clickSoundId Optional sound id to play on click.
     * @returns {Button}
     */
    createButton(skin, boundClickHandler, label, clickSoundId, shouldCenterLabel = true) {
        const button = new Button(skin);
        this._decorateButton(button, boundClickHandler, label, clickSoundId, shouldCenterLabel);

        return button;
    }

    /**
     * Convenience method for creating a Slice9Button
     * @param {DisplayObject} skin
     * @param {Function} boundClickHandler Optional handler method for click event.
     * @param {String} label Optional text label.
     * @param {type} clickSoundId Optional sound id to play on click.
     * @param {number} mode Expanding direction of the button. Default is Slice9Button.VERTICAL.
     * @returns {Slice9Button}
     */
    createSlice9Button(skin, boundClickHandler, label, clickSoundId, mode = Slice9Button.VERTICAL, options = { cacheBitmap: true, pixelPerfect: false, overlap: false }) {
        var button = new Slice9Button(skin, mode, options);
        this._decorateButton(button, boundClickHandler, label, clickSoundId, false);
        return button;
    }

    _decorateButton(button, boundClickHandler, label, clickSoundId, shouldCenterLabel) {
        if (boundClickHandler != null) {
            button.addEventListener("click", boundClickHandler);
        }
        if (label) {
            if (shouldCenterLabel) {
                button.setCenteredLabel(label);
            } else {
                button.setLabel(label);
            }
        }
        if (clickSoundId) {
            button.addEventListener("click", function () {
                this.playSound(clickSoundId);
            }.bind(this));
        }


        // TODO: not sure if 'decorate' should be responsible for this
        if (!this._buttons) this._buttons = [];
        this._buttons.push(button);
    }
}